import { useEffect, useState } from "react";
import { fetchSegments, createSegment, editSegment } from "./api";

const Segments = () => {
  const [segments, setSegments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSegment, setCurrentSegment] = useState({
    name: '',
    description: '',
    condition: {
      type: 'profilePropertyCondition',
      propertyName: '',
      comparisonOperator: 'equals',
      propertyValue: ''
    }
  });

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      const data = await fetchSegments();
      // Transform the data to ensure it has the correct structure
      const formattedSegments = data.map(segment => ({
        metadata: {
          id: segment.itemId || segment.metadata?.id,
          name: segment.name || segment.metadata?.name || 'Unnamed Segment',
          description: segment.description || segment.metadata?.description || ''
        },
        condition: {
          type: segment.condition?.type || 'profilePropertyCondition',
          parameterValues: {
            propertyName: segment.condition?.parameterValues?.propertyName || '',
            comparisonOperator: segment.condition?.parameterValues?.comparisonOperator || 'equals',
            propertyValue: segment.condition?.parameterValues?.propertyValue || ''
          }
        }
      }));
      setSegments(formattedSegments);
    } catch (error) {
      console.error('Error loading segments:', error);
      setSegments([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (isEditing) {
        response = await editSegment(currentSegment.metadata.id, currentSegment);
        const updatedSegments = segments.map(seg => 
          seg.metadata.id === currentSegment.metadata.id ? response : seg
        );
        setSegments(updatedSegments);
      } else {
        response = await createSegment(currentSegment);
        setSegments([...segments, response]);
      }
      handleCloseForm();
    } catch (error) {
      console.error('Error saving segment:', error);
    }
  };

  const handleEdit = (segment) => {
    setCurrentSegment({
      ...segment,
      name: segment.metadata.name,
      description: segment.metadata.description,
      condition: {
        type: segment.condition.type,
        propertyName: segment.condition.parameterValues.propertyName,
        comparisonOperator: segment.condition.parameterValues.comparisonOperator,
        propertyValue: segment.condition.parameterValues.propertyValue
      }
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setCurrentSegment({
      name: '',
      description: '',
      condition: {
        type: 'profilePropertyCondition',
        propertyName: '',
        comparisonOperator: 'equals',
        propertyValue: ''
      }
    });
  };

  return (
    <div className="segments-container">
      <div className="segments-header">
        <h1>Customer Segments</h1>
        <button 
          className="create-segment-btn"
          onClick={() => {
            setIsEditing(false);
            setShowForm(true);
          }}
        >
          Create New Segment
        </button>
      </div>

      {showForm && (
        <div className="create-segment-form">
          <h2>{isEditing ? 'Edit Segment' : 'Create New Segment'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Segment Name</label>
              <input
                type="text"
                value={currentSegment.name}
                onChange={(e) => setCurrentSegment({...currentSegment, name: e.target.value})}
                placeholder="Enter segment name"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={currentSegment.description}
                onChange={(e) => setCurrentSegment({...currentSegment, description: e.target.value})}
                placeholder="Enter segment description"
              />
            </div>

            <div className="form-group">
              <label>Property Name</label>
              <input
                type="text"
                value={currentSegment.condition.propertyName}
                onChange={(e) => setCurrentSegment({
                  ...currentSegment,
                  condition: {...currentSegment.condition, propertyName: e.target.value}
                })}
                placeholder="Enter property name"
                required
              />
            </div>

            <div className="form-group">
              <label>Comparison Operator</label>
              <select
                value={currentSegment.condition.comparisonOperator}
                onChange={(e) => setCurrentSegment({
                  ...currentSegment,
                  condition: {...currentSegment.condition, comparisonOperator: e.target.value}
                })}
              >
                <option value="equals">Equals</option>
                <option value="notEquals">Not Equals</option>
                <option value="greaterThan">Greater Than</option>
                <option value="lessThan">Less Than</option>
                <option value="contains">Contains</option>
              </select>
            </div>

            <div className="form-group">
              <label>Property Value</label>
              <input
                type="text"
                value={currentSegment.condition.propertyValue}
                onChange={(e) => setCurrentSegment({
                  ...currentSegment,
                  condition: {...currentSegment.condition, propertyValue: e.target.value}
                })}
                placeholder="Enter property value"
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {isEditing ? 'Update Segment' : 'Create Segment'}
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={handleCloseForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="segments-list">
        {segments.map((segment) => (
          <div key={segment.metadata.id || Math.random()} className="segment-card">
            <div className="segment-info">
              <h3>{segment.metadata.name}</h3>
              <p>{segment.metadata.description}</p>
            </div>
            <div className="segment-stats">
              <div className="stat">
                <span className="stat-label">Property</span>
                <span className="stat-value">
                  {segment.condition?.parameterValues?.propertyName || 'N/A'}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Operator</span>
                <span className="stat-value">
                  {segment.condition?.parameterValues?.comparisonOperator || 'N/A'}
                </span>
              </div>
            </div>
            <div className="segment-actions">
              <button 
                className="edit-btn"
                onClick={() => handleEdit(segment)}
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Segments;
